import React, { useContext, useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'

import '../territory.css'
import '../../../components/Home/homePanel.css'

import HanList from '../../../components/HanList/HanList'
import AdventureList from '../../../components/AdventureList/AdventureList'
import FiltersBar from '../../../components/AdventureList/FiltersBar'
// WIP: FeaturedAdventureCard — reactivar cuando haya criterio de “destacada”
import AppContext from '../../../contexts/AppContext'
import HttpClient from '../../../services/HttpClient'
import MissionBoard from '../../../features/missions/board/MissionBoard'
import HexMap from '../../../components/HexMap/HexMap'
import { fetchTerritoryControlLayers } from '../../../services/territoryControl.api'
import TerritoryHero from './TerritoryHero'
import TravelPanel from '../../../features/travel/TravelPanel'
import { fetchTravelRoutes } from '../../../features/travel/api'
import { resolveAdventureStatus, resolveAdventureMissionType } from '../../../features/adventure/shared/adventureConstants'
import { ensureStatsReadyForRoleplay } from '../../../utils/statsReallocGate'

import '../../../components/AdventureList/AdventureList.css'

export default function ViewTerritory() {
  const navigate = useNavigate()
  const { user, character, setCharacter } = useContext(AppContext)
  const { slug } = useParams()
  const [territory, setTerritory] = useState(null)
  const [adventures, setAdventures] = useState([])
  const [capital, setCapital] = useState(null)
  const [canTravelHere, setCanTravelHere] = useState(false)
  const [showTravelPanel, setShowTravelPanel] = useState(false)

  const [hanCells, setHanCells] = useState([])
  const [empireCells, setEmpireCells] = useState([])
  const [clanCells, setClanCells] = useState([])
  const [worldEmpireCells, setWorldEmpireCells] = useState([])
  const [worldHanCells, setWorldHanCells] = useState([])
  const [filters, setFilters] = useState({
    query: '',
    missionTypes: [],
    statuses: [],
    difficulties: [],
    sort: 'recientes',
  })
  const [selectedCell, setSelectedCell] = useState(null)
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  const selectedHighlight = useMemo(() => {
    const q = Number(selectedCell?.q)
    const r = Number(selectedCell?.r)
    return Number.isFinite(q) && Number.isFinite(r) ? { q, r } : null
  }, [selectedCell])

  const clanIndexMap = useMemo(() => {
    const m = new Map()
    for (const c of clanCells || []) {
      const q = Number(c?.q)
      const r = Number(c?.r)
      if (Number.isFinite(q) && Number.isFinite(r)) m.set(`${q},${r}`, c)
    }
    return m
  }, [clanCells])

  const clanDict = useMemo(() => {
    const d = {}
    const collect = (arr) => {
      ;(arr || []).forEach((c) => {
        if (c?.clanId != null && (c?.clanName || c?.clanSlug)) {
          d[String(c.clanId)] = c.clanName || c.clanSlug
        }
      })
    }
    collect(hanCells)
    collect(clanCells)
    return d
  }, [hanCells, clanCells])

  const handleCellClick = (cell) => {
    if (!cell || cell.__layer !== 'han') return
    let next = { ...cell }
    const q = Number(cell.q)
    const r = Number(cell.r)
    if (Number.isFinite(q) && Number.isFinite(r)) {
      const cc = clanIndexMap.get(`${q},${r}`)
      const effClanId = cell.clanId != null ? cell.clanId : cc?.clanId ?? null
      const effClanName =
        cell.__clanName ||
        cell.clanName ||
        cell.clanSlug ||
        (effClanId != null ? clanDict[String(effClanId)] || cc?.clanName || cc?.clanSlug : null)
      next = {
        ...next,
        clanId: effClanId != null ? effClanId : next.clanId,
        clanName: effClanName || next.clanName,
        __clanName: effClanName || next.__clanName,
      }
    }
    setSelectedCell(next)
    setIsPanelOpen(true)
  }

  const handleClosePanel = () => {
    setIsPanelOpen(false)
    setSelectedCell(null)
  }

  useEffect(() => {
    setTerritory(null)
    setAdventures([])
    setHanCells([])
    setEmpireCells([])
    setClanCells([])
    setWorldHanCells([])
    setCapital(null)
    setCanTravelHere(false)
    setShowTravelPanel(false)
    getTerritory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  useEffect(() => {
    let cancelled = false
    const checkHome = async () => {
      const characterId = character?.characterId
      if (!characterId || !slug || character?.academyMode) {
        if (!cancelled) setCanTravelHere(false)
        return
      }
      const homeFromChar = character.current_territory_slug || character.currentTerritorySlug
      if (homeFromChar && String(homeFromChar) === String(slug)) {
        if (!cancelled) setCanTravelHere(true)
        return
      }
      try {
        const { originSlug } = await fetchTravelRoutes(characterId)
        if (!cancelled) setCanTravelHere(!!originSlug && String(originSlug) === String(slug))
      } catch {
        if (!cancelled) setCanTravelHere(false)
      }
    }
    checkHome()
    return () => {
      cancelled = true
    }
  }, [character, slug])

  const getTerritory = async () => {
    try {
      const { data } = await HttpClient().get('/api/v1/territory/' + slug)
      if (data) {
        setTerritory(data)
        getAdventures()
        loadTerritoryControl()
        loadCapital()
      } else {
        setTerritory(null)
      }
    } catch (error) {
      console.error('Error fetching territory:', error)
      setTerritory(null)
    }
  }

  const getAdventures = async () => {
    try {
      const { data } = await HttpClient().get('/api/v1/adventure/enhanced?slug=' + slug)
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []
      setAdventures(list)
    } catch (error) {
      if (error?.status === 404) {
        setAdventures([])
        return
      }
      try {
        const { data } = await HttpClient().get('/api/v1/adventure?slug=' + slug)
        const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []
        setAdventures(list)
      } catch (fallbackError) {
        setAdventures([])
      }
    }
  }

  const loadTerritoryControl = async () => {
    try {
      const { data } = await HttpClient().get('/api/v1/territory/control', {
        params: { slug, layer: 'both' },
      })
      const han = Array.isArray(data?.han) ? data.han : []
      const emp = Array.isArray(data?.empire) ? data.empire : []
      setHanCells(
        han.map((c) => ({
          territorySlug: c.territorySlug,
          territoryName: c.territoryName,
          q: Number(c.q),
          r: Number(c.r),
          status: c.status,
          clanId: c.clanId,
          clanSlug: c.clanSlug,
          clanName: c.clanName,
          clanColor: c.clanColor,
          hanColor: c.hanColor,
          layer: 'han',
          since: c.since,
        }))
      )
      setEmpireCells(
        emp.map((c) => ({
          territorySlug: c.territorySlug,
          territoryName: c.territoryName,
          q: Number(c.q),
          r: Number(c.r),
          status: c.status,
          empireSlug: c.empireSlug,
          empireName: c.empireName,
          empireColor: c.empireColor,
          shogunatoId: c.shogunatoId,
          hanId: c.hanId,
          layer: 'empire',
          since: c.since,
        }))
      )
      const cln = Array.isArray(data?.clan) ? data.clan : []
      setClanCells(
        cln.map((c) => ({
          territorySlug: c.territorySlug,
          territoryName: c.territoryName,
          q: Number(c.q),
          r: Number(c.r),
          clanId: c.clanId,
          clanSlug: c.clanSlug,
          clanName: c.clanName,
          clanColor: c.clanColor,
          layer: 'clan',
          since: c.since,
        }))
      )
      try {
        const layers = await fetchTerritoryControlLayers({ all: true })
        setWorldEmpireCells(Array.isArray(layers?.empire) ? layers.empire : [])
        setWorldHanCells(Array.isArray(layers?.han) ? layers.han : [])
      } catch {
        setWorldEmpireCells([])
      }
    } catch (e) {
      console.error('Error loading territory control:', e)
      setHanCells([])
      setEmpireCells([])
      setWorldEmpireCells([])
    }
  }

  const loadCapital = async () => {
    try {
      const { data } = await HttpClient().get('/api/v1/territory/capital/' + slug)
      setCapital(data?.slug ? data : null)
    } catch (e) {
      if (e?.response?.status !== 404) {
        console.warn('No se pudo cargar la capital:', e?.message || e)
      }
      setCapital(null)
    }
  }

  const hanPalette = useMemo(() => {
    const byId = {}
    const bySlug = {}
    for (const c of hanCells || []) {
      const color = c?.hanColor
      if (!color) continue
      if (c?.hanId != null) byId[String(c.hanId)] = color
      if (c?.id != null) byId[String(c.id)] = color
      if (c?.hanSlug) bySlug[String(c.hanSlug)] = color
      if (c?.territorySlug) bySlug[String(c.territorySlug)] = color
    }
    return { byId, bySlug }
  }, [hanCells])

  const fitCells = useMemo(() => {
    if (hanCells?.length > 0) return hanCells
    const empireSrc = worldEmpireCells.length ? worldEmpireCells : empireCells
    return (empireSrc || []).filter((c) => String(c?.territorySlug) === String(slug))
  }, [hanCells, worldEmpireCells, empireCells, slug])

  const fitBBoxKey = useMemo(() => {
    const list = fitCells || []
    if (list.length === 0) return 'no-fit'
    let minQ = Infinity, minR = Infinity, maxQ = -Infinity, maxR = -Infinity
    for (const c of list) {
      const qv = Number(c?.q)
      const rv = Number(c?.r)
      if (!Number.isFinite(qv) || !Number.isFinite(rv)) continue
      if (qv < minQ) minQ = qv
      if (rv < minR) minR = rv
      if (qv > maxQ) maxQ = qv
      if (rv > maxR) maxR = rv
    }
    if (!Number.isFinite(minQ)) return 'invalid'
    return `${minQ},${minR}-${maxQ},${maxR}`
  }, [fitCells])

  const filteredAdventures = useMemo(() => {
    let list = adventures || []
    const q = (filters.query || '').toLowerCase().trim()
    if (q) {
      list = list.filter(
        (a) =>
          String(a.title || '').toLowerCase().includes(q) ||
          String(a.author || '').toLowerCase().includes(q)
      )
    }
    if (filters.missionTypes?.length) list = list.filter((a) => filters.missionTypes.includes(resolveAdventureMissionType(a)))
    if (filters.statuses?.length) {
      list = list.filter((a) => filters.statuses.includes(resolveAdventureStatus(a)))
    }
    if (filters.difficulties?.length) list = list.filter((a) => filters.difficulties.includes(a.difficulty))
    switch (filters.sort) {
      case 'actividad':
        list = [...list].sort(
          (a, b) => Number(b.replies_count || 0) - Number(a.replies_count || 0) || Number(b.views || 0) - Number(a.views || 0)
        )
        break
      case 'participantes':
        list = [...list].sort((a, b) => Number(b.current_participants || 0) - Number(a.current_participants || 0))
        break
      default:
        list = [...list].sort((a, b) => new Date(b.last_activity || 0) - new Date(a.last_activity || 0))
    }
    return list
  }, [adventures, filters])

  const empireFocusSlug = useMemo(() => {
    if (!empireCells?.length) return null
    const counts = new Map()
    for (const c of empireCells) {
      const s = c?.empireSlug || c?.shogunateSlug || c?.territoryEmpireSlug || c?.empireName || null
      if (!s) continue
      const k = String(s).trim().toLowerCase()
      if (k) counts.set(k, (counts.get(k) || 0) + 1)
    }
    let best = null
    let bestCount = -1
    for (const [k, v] of counts) {
      if (v > bestCount) { best = k; bestCount = v }
    }
    return best
  }, [empireCells])

  const capitalFocus = useMemo(() => {
    const q = Number(capital?.capital_q)
    const r = Number(capital?.capital_r)
    if (!Number.isFinite(q) || !Number.isFinite(r)) return null
    const isCapitalPage = territory && capital && String(territory.slug).toLowerCase() === String(capital.slug).toLowerCase()
    const isEmpireRoot = territory?.territory_type === 'Shogunato' || Number(territory?.parentId || 0) === 0
    return isCapitalPage || isEmpireRoot ? { q, r } : null
  }, [territory, capital])

  const resolvedTerritoryType = useMemo(() => {
    const raw = String(territory?.territory_type || '').trim()
    if (raw) return raw
    return Number(territory?.parentId || 0) > 0 ? 'Han' : 'Shogunato'
  }, [territory])

  const missionTitle = useMemo(() => {
    const t = resolvedTerritoryType
    if (t === 'Shogunato') return 'Misiones imperiales'
    if (t === 'Han') return 'Misiones del han'
    return 'Tablón de misiones'
  }, [resolvedTerritoryType])

  const isShogunato = resolvedTerritoryType === 'Shogunato'
  const isHan = resolvedTerritoryType === 'Han'
  const showCapital = capital && String(territory?.slug) !== String(capital?.slug)

  if (!territory) {
    return (
      <div className="territory-page parchment-scope">
        <div className="territory-loading">
          <div className="territory-loading__spinner" aria-hidden="true" />
          <span>Consultando los archivos del reino…</span>
        </div>
      </div>
    )
  }

  return (
    <div className="territory-page parchment-scope">
      <TerritoryHero
        territory={territory}
        adventureCount={adventures.length}
        canCreate={!!user}
        canTravel={canTravelHere}
        onCreateAdventure={async () => {
          if (
            !(await ensureStatsReadyForRoleplay({
              character,
              navigate,
              setCharacter,
            }))
          ) {
            return
          }
          navigate(`/aventura/crear/${slug}`)
        }}
        onTravel={() => setShowTravelPanel(true)}
      />

      <div className="territory-page__body">
        {showTravelPanel && canTravelHere && character?.characterId ? (
          <section className="territory-panel home-bento-card" aria-label="Iniciar viaje">
            <header className="home-panel__header">
              <span className="home-panel__eyebrow">Tránsito</span>
              <h2 className="home-panel__title">Viajar desde aquí</h2>
            </header>
            <TravelPanel
              characterId={character.characterId}
              homeSlug={slug}
              mode="full"
            />
          </section>
        ) : null}

        {/* Misiones — estudiantes: tablón cerrado (Estudiante E) */}
        {!character?.academyMode ? (
          <section className="territory-panel home-bento-card home-bento-card--pulse">
            <header className="home-panel__header">
              <span className="home-panel__eyebrow">Tablón</span>
              <h2 className="home-panel__title">{missionTitle}</h2>
            </header>
            <MissionBoard
              territorySlug={slug}
              territoryType={resolvedTerritoryType}
              user={user}
              character={character}
            />
          </section>
        ) : (
          <section
            className="territory-panel home-bento-card territory-panel--missions-locked"
            aria-labelledby="territory-missions-locked-title"
          >
            {/* Capa inferior: tablón “fantasma” difuminado */}
            <div className="territory-missions-locked__backdrop" aria-hidden="true">
              <div className="territory-missions-locked__ghost-head">
                <span className="home-panel__eyebrow">Tablón</span>
                <span className="home-panel__title">{missionTitle}</span>
              </div>
              <div className="territory-missions-locked__ghost-list">
                <div className="territory-missions-locked__ghost-row">
                  <span className="territory-missions-locked__ghost-rank" />
                  <span className="territory-missions-locked__ghost-lines">
                    <i />
                    <i />
                  </span>
                  <span className="territory-missions-locked__ghost-btn" />
                </div>
                <div className="territory-missions-locked__ghost-row">
                  <span className="territory-missions-locked__ghost-rank" />
                  <span className="territory-missions-locked__ghost-lines">
                    <i />
                    <i />
                  </span>
                  <span className="territory-missions-locked__ghost-btn" />
                </div>
                <div className="territory-missions-locked__ghost-row">
                  <span className="territory-missions-locked__ghost-rank" />
                  <span className="territory-missions-locked__ghost-lines">
                    <i />
                    <i />
                  </span>
                  <span className="territory-missions-locked__ghost-btn" />
                </div>
              </div>
            </div>

            {/* Capa superior: mensaje nítido */}
            <div className="territory-missions-locked" role="status">
              <header className="territory-missions-locked__header">
                <div className="territory-missions-locked__titles">
                  <span className="home-panel__eyebrow">Academia</span>
                  <h2 className="home-panel__title" id="territory-missions-locked-title">
                    Tablón sellado
                  </h2>
                </div>
                <span className="territory-missions-locked__seal">封</span>
              </header>

              <p className="territory-missions-locked__lead">
                {isHan
                  ? 'Mientras eres estudiante, las misiones del han permanecen selladas. Primero dominas el circuito de academia.'
                  : 'El tablón de misiones no admite estudiantes. Primero completas el tutorial de academia.'}
              </p>
              <ul className="territory-missions-locked__list">
                <li>Entrena técnicas y rol en escenas de academia</li>
                <li>Las misiones de sensei o staff llegan por asignación directa</li>
                <li>Al graduarte como Genin se abre este tablón</li>
              </ul>
            </div>
          </section>
        )}

        {/* Capital */}
        {showCapital && (
          <section className="territory-panel territory-panel--capital home-bento-card home-bento-card--territories">
            <header className="home-panel__header territory-panel__header-row">
              <div>
                <span className="home-panel__eyebrow">Sede del imperio</span>
                <h2 className="home-panel__title">Capital</h2>
              </div>
              <Link to={`/han/${capital.slug}`} className="territory-capital-link">
                <span aria-hidden="true">⛩</span>
                {capital.name}
                <span aria-hidden="true">→</span>
              </Link>
            </header>
          </section>
        )}

        {/* Hans del imperio */}
        {isShogunato && (
          <section className="territory-panel home-bento-card home-bento-card--territories">
            <header className="home-panel__header">
              <span className="home-panel__eyebrow">Dominios</span>
              <h2 className="home-panel__title">Hans del imperio</h2>
            </header>
            <HanList parentSlug={slug} />
          </section>
        )}

        {/* Distritos — próximamente */}
        {isHan && (
          <section className="territory-panel home-bento-card home-bento-card--territories">
            <header className="home-panel__header">
              <span className="home-panel__eyebrow">Divisiones</span>
              <h2 className="home-panel__title">Distritos</h2>
            </header>
            <div className="territory-soon" role="status">
              <span className="territory-soon__seal" aria-hidden="true">
                区
              </span>
              <p className="territory-soon__eyebrow">En preparación</p>
              <h3 className="territory-soon__title">Distritos próximamente</h3>
              <p className="territory-soon__text">
                Las divisiones del han — barrios, puestos y ruinas — llegarán en una entrega
                posterior. Mientras tanto, explora el mapa del territorio más abajo.
              </p>
            </div>
          </section>
        )}

        {/* Mapa hexagonal */}
        <section className="territory-panel territory-panel--map home-bento-card home-bento-card--featured">
          <header className="home-panel__header">
            <span className="home-panel__eyebrow">Cartografía</span>
            <h2 className="home-panel__title">Mapa del territorio</h2>
            <p className="home-panel__subtitle">Haz clic en una celda para ver su información</p>
          </header>
          <div className="territory-map-shell">
            <div className="territory-map-frame">
              <HexMap
                key={`territory-map|${slug}|bbox=${fitBBoxKey}|han=${hanCells.length}`}
                hanCells={worldHanCells.length ? worldHanCells : hanCells}
                empireCells={worldEmpireCells.length ? worldEmpireCells : empireCells}
                clanCells={clanCells}
                hanPalette={hanPalette}
                clanDict={clanDict}
                showHan={true}
                showEmpire={true}
                activeLayer="han"
                fitToCells={fitCells}
                hexSize={16}
              showGrid={true}
              showStatusTint={true}
              toolMode="move"
                onCellClick={handleCellClick}
                empireFocusSlug={empireFocusSlug}
                empireDimOpacity={0.18}
                hanFocusSlug={isHan ? slug : null}
                hanDimOpacity={0.18}
                focusHex={capitalFocus}
                focusZoom={capitalFocus ? 2.5 : null}
                highlightHex={selectedHighlight || capitalFocus}
                markers={
                  Number.isFinite(Number(capital?.capital_q)) && Number.isFinite(Number(capital?.capital_r))
                    ? [{ q: Number(capital.capital_q), r: Number(capital.capital_r), icon: '⛩' }]
                    : []
                }
              />
            </div>

            {isPanelOpen && selectedCell && (
              <aside className="territory-cell-panel" role="dialog" aria-modal="true" aria-label="Información de celda">
                <header className="territory-cell-panel__header">
                  <div className="territory-cell-panel__heading">
                    <span className="territory-cell-panel__eyebrow">Celda</span>
                    <h3 className="territory-cell-panel__title">
                      {selectedCell.hanName || selectedCell.hanSlug || selectedCell.territoryName || selectedCell.territorySlug || 'Territorio'}
                    </h3>
                  </div>
                  <button type="button" className="territory-cell-panel__close" onClick={handleClosePanel} aria-label="Cerrar">
                    ✕
                  </button>
                </header>

                <div className="territory-cell-panel__body">
                  <div className="territory-cell-panel__status-row">
                    <span className="territory-cell-panel__status">
                      {selectedCell.status || 'Sin estado'}
                    </span>
                    <span className="territory-cell-panel__coords">
                      ({Number(selectedCell.q)}, {Number(selectedCell.r)})
                    </span>
                  </div>

                  <dl className="territory-cell-panel__fields">
                    <div className="territory-cell-panel__field">
                      <dt>Clan</dt>
                      <dd>
                        {selectedCell.__clanName || selectedCell.clanName || selectedCell.clanSlug ||
                          (selectedCell.clanId != null ? `Clan ${selectedCell.clanId}` : 'Sin clan')}
                      </dd>
                    </div>
                    {selectedCell.since && (
                      <div className="territory-cell-panel__field">
                        <dt>Desde</dt>
                        <dd>{new Date(selectedCell.since).toLocaleDateString()}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </aside>
            )}
          </div>
        </section>

        {/* Aventuras */}
        <section className="territory-panel territory-panel--adventures home-bento-card home-bento-card--activity">
          <header className="home-panel__header territory-panel__header-row">
            <div>
              <span className="home-panel__eyebrow">Crónicas</span>
              <h2 className="home-panel__title">
                Aventuras de {territory.name || 'este territorio'}
              </h2>
            </div>
            {adventures.length > 0 && (
              <span className="territory-count-badge">
                {adventures.length} aventura{adventures.length !== 1 ? 's' : ''}
              </span>
            )}
          </header>

          <FiltersBar
            filters={filters}
            onChangeFilters={setFilters}
            onClear={() => setFilters({ query: '', missionTypes: [], statuses: [], difficulties: [], sort: 'recientes' })}
            collapsibleChips={true}
            defaultCollapsed={true}
            showActiveCount={true}
            collapsedLabel="Filtrar"
          />

          <AdventureList adventures={filteredAdventures} layout="list" variant="compact" />
        </section>
      </div>
    </div>
  )
}
